import { toast } from 'react-toastify';

import { Button, getNotificationContent } from 'components';
import { SCAN_NAMES, TOKEN_BY_ADDRESS } from 'consts';
import { store } from 'store';
import { getErc20Abi, getScanUrl } from 'utils/helpers';
import { handleWeb3Error } from 'utils/helpers/handleError';
import { Address, TransactionReceipt } from 'viem';
import { usePublicClient, useWriteContract } from 'wagmi';

import { useTransactionExpired } from './useTransactionExpired';

type Props = {
  chainId: number | undefined;
  tokenAddress: Address | undefined | null;
  spenderAddress: Address | undefined | null;
  allowance: number;
};

const SHOULD_RESET: Record<number, Address[]> = {
  1: ['0xdac17f958d2ee523a2206206994597c13d831ec7'], // USDT
  8453: ['0xc0634090f2fe6c6d75e61be2b949464abb498973'],
};

export const useApprove = (props: Props) => {
  const { chainId, tokenAddress, spenderAddress, allowance } = props;
  const { account } = store.web3Store;

  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient({ chainId });
  const { setExpiredTimer, clearExpiredTimer } = useTransactionExpired();

  let hasPreviousAllowance = !!allowance;

  const approve = async (amount: string): Promise<boolean | void> => {
    if (!chainId || !tokenAddress || !spenderAddress || !publicClient) {
      console.log('Approve: incorrect data', { tokenAddress, spenderAddress, publicClient });
      toast.error(
        `Approve: incorrect data, tokenAddress: ${tokenAddress};  spenderAddress: ${spenderAddress}; publicClient: ${!!publicClient}`
      );
      return;
    }

    const params = {
      account,
      abi: getErc20Abi(chainId, tokenAddress),
      address: tokenAddress,
      functionName: 'approve',
      args: [spenderAddress, BigInt(amount)],
      chainId,
    } as const;

    if (hasPreviousAllowance) {
      let shouldReset = SHOULD_RESET[chainId]?.includes(tokenAddress);

      if (!shouldReset) {
        try {
          await publicClient.estimateContractGas(params);
        } catch (err: any) {
          console.log('approve estimation error', { ...err });
          if (err.shortMessage?.includes('Unsafe allowance change')) {
            shouldReset = true;
          }
        }
      }

      if (shouldReset) {
        toast.info(
          `To change the approved amount for ${TOKEN_BY_ADDRESS[chainId][tokenAddress]?.symbol || 'token'}, first set it to 0, then set the new amount. This needs two transactions.`,
          {
            position: 'top-center',
            autoClose: 10_000,
          }
        );
        hasPreviousAllowance = false;
        const isReset = await approve('0');
        if (isReset) await approve(amount);
        return;
      }
    }

    let isSuccess = false;
    setExpiredTimer();

    try {
      const hash = await writeContractAsync(params);

      const receipt: TransactionReceipt = await publicClient.waitForTransactionReceipt({
        hash,
      });

      if (receipt.status === 'success') {
        toast.success('Approve succeeded!');
        isSuccess = true;
      }

      if (receipt.status === 'reverted') {
        console.log('approve receipt', receipt);
        const scanLink = getScanUrl(`/tx/${hash}`, chainId);

        toast.error(
          getNotificationContent({
            isError: true,
            title: 'Failed to approve',
            href: scanLink,
            scanName: SCAN_NAMES[chainId],
          })
        );
      }
    } catch (err: any) {
      handleWeb3Error({ err, source: 'approve' });

      if (amount !== '0' && hasPreviousAllowance) {
        toast.info(
          <>
            <div style={{ marginBottom: 16 }}>
              Some tokens may require multiple transactions to update the allowance. First, you need
              to reset the allowance to zero, then set the new value.
            </div>
            <Button isFullWidth size="small" onClick={() => approve('0')}>
              Reset allowance
            </Button>
          </>,
          {
            autoClose: false,
          }
        );
      }
    }

    clearExpiredTimer();
    return isSuccess;
  };

  return approve;
};
